<script setup>
import { reactive, ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { required, minLength, sameAs, helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import Avatar from '@/components/Avatar.vue'
import NextInput from '@/components/Form/NextInput.vue'
import Button from '@/components/Button.vue'
import { useAlert } from '@/composables/useToast'

const store = useStore()
const { t } = useI18n()

const currentUser = computed(() => store.getters['auth/getCurrentUser'])

// Operator photo; the company logo is shown while it is empty
const avatarInput = ref(null)
const savingAvatar = ref(false)

const onAvatarSelected = async event => {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  savingAvatar.value = true
  try {
    const payload = new FormData()
    payload.append('avatar', file)
    await store.dispatch('auth/updateProfile', payload)
    useAlert(t('PROFILE.AVATAR.SUCCESS'))
  } catch (error) {
    useAlert(t('PROFILE.AVATAR.ERROR'))
  } finally {
    savingAvatar.value = false
  }
}

const removeAvatar = async () => {
  savingAvatar.value = true
  try {
    await store.dispatch('auth/updateProfile', { remove_avatar: true })
    useAlert(t('PROFILE.AVATAR.REMOVED'))
  } catch (error) {
    useAlert(t('PROFILE.AVATAR.ERROR'))
  } finally {
    savingAvatar.value = false
  }
}

const profileForm = reactive({ name: '' })
watch(
  currentUser,
  user => {
    if (user) profileForm.name = user.name
  },
  { immediate: true }
)

const profileRules = { name: { required } }
const vProfile = useVuelidate(profileRules, profileForm)
const savingProfile = ref(false)

const saveProfile = async () => {
  vProfile.value.$touch()
  if (vProfile.value.$invalid) return
  savingProfile.value = true
  try {
    await store.dispatch('auth/updateProfile', { name: profileForm.name })
    useAlert(t('PROFILE.NAME.SUCCESS'))
  } catch (error) {
    useAlert(t('PROFILE.NAME.ERROR'))
  } finally {
    savingProfile.value = false
  }
}

// Personal sending hotkey; Enter stays a new line unless the operator picks it
const SEND_HOTKEYS = ['cmd_enter', 'enter']
const sendHotKey = ref('cmd_enter')
watch(
  currentUser,
  user => {
    if (user)
      sendHotKey.value = user.ui_settings?.editor_message_key || 'cmd_enter'
  },
  { immediate: true }
)

const setSendHotKey = async key => {
  if (key === sendHotKey.value) return
  const previous = sendHotKey.value
  sendHotKey.value = key
  try {
    await store.dispatch('auth/updateProfile', {
      ui_settings: { editor_message_key: key },
    })
    useAlert(t('PROFILE.SEND_KEY.SUCCESS'))
  } catch (error) {
    sendHotKey.value = previous
    useAlert(t('PROFILE.SEND_KEY.ERROR'))
  }
}

const passwordForm = reactive({
  current_password: '',
  password: '',
  password_confirmation: '',
})

// Mirrors the server rule: at least 8 characters with letters and digits
const lettersAndDigits = helpers.regex(/^(?=.*\p{L})(?=.*\d).*$/u)

const passwordRules = computed(() => ({
  current_password: { required },
  password: { required, minLength: minLength(8), lettersAndDigits },
  password_confirmation: {
    required,
    sameAs: sameAs(passwordForm.password),
  },
}))
const vPassword = useVuelidate(passwordRules, passwordForm)
const savingPassword = ref(false)

const savePassword = async () => {
  vPassword.value.$touch()
  if (vPassword.value.$invalid) return
  savingPassword.value = true
  try {
    await store.dispatch('auth/updateProfile', {
      current_password: passwordForm.current_password,
      password: passwordForm.password,
      password_confirmation: passwordForm.password_confirmation,
    })
    useAlert(t('PROFILE.PASSWORD.SUCCESS'))
    passwordForm.current_password = ''
    passwordForm.password = ''
    passwordForm.password_confirmation = ''
    vPassword.value.$reset()
  } catch (error) {
    useAlert(
      error?.response?.status === 401
        ? t('PROFILE.PASSWORD.INVALID_CURRENT')
        : t('PROFILE.PASSWORD.ERROR')
    )
  } finally {
    savingPassword.value = false
  }
}
</script>

<template>
  <div
    class="flex flex-col w-full h-full px-6 pt-4 pb-8 m-0 overflow-auto bg-n-surface-1"
  >
    <div class="flex items-start w-full mx-auto max-w-5xl">
      <div class="flex flex-col w-full h-full gap-8 font-inter">
        <div class="flex flex-col gap-2">
          <h1 class="text-heading-1 text-n-slate-12">
            {{ $t('PROFILE.TITLE') }}
          </h1>
          <p class="text-body-main text-n-slate-11">
            {{ $t('PROFILE.DESCRIPTION') }}
          </p>
        </div>

        <section
          class="flex flex-col max-w-lg gap-3 pt-6 border-t border-n-weak"
        >
          <h2 class="text-heading-3 text-n-slate-12">
            {{ $t('PROFILE.AVATAR.TITLE') }}
          </h2>
          <div class="flex items-center gap-4">
            <Avatar
              v-if="currentUser"
              :name="currentUser.available_name || currentUser.name"
              :size="72"
              :src="currentUser.avatar_url"
              logo-fallback
            />
            <div class="flex items-center gap-2">
              <Button
                type="button"
                sm
                faded
                slate
                :label="$t('PROFILE.AVATAR.UPLOAD')"
                :disabled="savingAvatar"
                @click="avatarInput?.click()"
              />
              <Button
                v-if="currentUser?.avatar_url"
                type="button"
                sm
                ghost
                ruby
                :label="$t('PROFILE.AVATAR.REMOVE')"
                :disabled="savingAvatar"
                @click="removeAvatar"
              />
            </div>
            <input
              ref="avatarInput"
              type="file"
              accept="image/*"
              class="hidden"
              @change="onAvatarSelected"
            />
          </div>
          <p class="text-body-main text-n-slate-11">
            {{ $t('PROFILE.AVATAR.DESC') }}
          </p>
        </section>

        <form
          class="flex flex-col max-w-lg gap-5 pt-6 border-t border-n-weak"
          @submit.prevent="saveProfile"
        >
          <NextInput
            v-model="profileForm.name"
            name="profile_name"
            type="text"
            :label="$t('PROFILE.NAME.LABEL')"
            :has-error="vProfile.name.$error"
            @input="vProfile.name.$touch"
          />
          <div class="flex flex-col gap-1">
            <span class="text-heading-3 text-n-slate-12">
              {{ $t('PROFILE.EMAIL.LABEL') }}
            </span>
            <p class="text-sm text-n-slate-11">{{ currentUser?.email }}</p>
          </div>
          <div>
            <Button
              type="submit"
              :label="$t('PROFILE.SAVE')"
              :disabled="savingProfile"
            />
          </div>
        </form>

        <section
          class="flex flex-col max-w-lg gap-3 pt-6 border-t border-n-weak"
        >
          <div class="flex flex-col gap-1">
            <h2 class="text-heading-2 text-n-slate-12">
              {{ $t('PROFILE.SEND_KEY.TITLE') }}
            </h2>
            <p class="text-body-main text-n-slate-11">
              {{ $t('PROFILE.SEND_KEY.DESC') }}
            </p>
          </div>
          <label
            v-for="key in SEND_HOTKEYS"
            :key="key"
            class="flex items-start gap-3 p-4 rounded-xl outline outline-1 cursor-pointer bg-n-solid-1"
            :class="
              sendHotKey === key
                ? 'outline-n-blue-9'
                : 'outline-n-weak hover:outline-n-strong'
            "
          >
            <input
              type="radio"
              name="send_hot_key"
              class="mt-0.5 shadow cursor-pointer grid place-items-center border-2 border-n-strong appearance-none rounded-full w-5 h-5 shrink-0 checked:bg-n-brand before:content-[''] before:bg-n-brand before:border-4 before:rounded-full before:border-n-strong checked:before:w-[18px] checked:before:h-[18px] checked:border checked:border-n-brand"
              :checked="sendHotKey === key"
              @change="setSendHotKey(key)"
            />
            <span class="flex flex-col gap-1">
              <span class="text-heading-3 text-n-slate-12">
                {{ $t(`PROFILE.SEND_KEY.${key.toUpperCase()}.TITLE`) }}
              </span>
              <span class="text-body-main text-n-slate-11">
                {{ $t(`PROFILE.SEND_KEY.${key.toUpperCase()}.DESC`) }}
              </span>
            </span>
          </label>
        </section>

        <form
          class="flex flex-col max-w-lg gap-5 pt-6 border-t border-n-weak"
          @submit.prevent="savePassword"
        >
          <h2 class="text-heading-3 text-n-slate-12">
            {{ $t('PROFILE.PASSWORD.SECTION') }}
          </h2>
          <NextInput
            v-model="passwordForm.current_password"
            name="current_password"
            type="password"
            :label="$t('PROFILE.PASSWORD.CURRENT')"
            :has-error="vPassword.current_password.$error"
            @input="vPassword.current_password.$touch"
          />
          <NextInput
            v-model="passwordForm.password"
            name="new_password"
            type="password"
            :label="$t('PROFILE.PASSWORD.NEW')"
            :has-error="vPassword.password.$error"
            @input="vPassword.password.$touch"
          />
          <NextInput
            v-model="passwordForm.password_confirmation"
            name="confirm_password"
            type="password"
            :label="$t('PROFILE.PASSWORD.CONFIRM')"
            :has-error="vPassword.password_confirmation.$error"
            @input="vPassword.password_confirmation.$touch"
          />
          <div>
            <Button
              type="submit"
              :label="$t('PROFILE.PASSWORD.SUBMIT')"
              :disabled="savingPassword"
            />
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
